/**
 * Workout Card Share Sheet — shown after workout completion.
 * Renders WorkoutCard, lets user edit caption, posts to community,
 * uses Web Share API for native share (WhatsApp/Instagram/X), and
 * downloads PNG via html2canvas.
 *
 * Props:
 *   - session: WorkoutSession object
 *   - onClose: () => void
 *   - onPosted: () => void
 *   - onSkip: () => void  // tracks 3-skip dismissal
 */
import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Download, Share2 } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import WorkoutCard from "./WorkoutCard";

export default function WorkoutCardShareSheet({ session, onClose, onPosted, onSkip }) {
  const user = useAuthStore((s) => s.user);
  const cardRef = useRef(null);
  const defaultCaption =
    `${session?.total_sets ?? 0} sets. ${session?.total_reps ?? 0} reps. ${session?.duration_minutes ?? 0} min. Done. 💪`;
  const [caption, setCaption] = useState(defaultCaption);
  const [busy, setBusy] = useState(false);

  const renderPng = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
    });
    return canvas.toDataURL("image/png");
  };

  const shareToCommunity = async () => {
    setBusy(true);
    try {
      await apiClient.post("/feed/posts", {
        text: caption.slice(0, 280),
        hub: user?.city || "all",
        post_type: "workout_card",
        workout_session_id: session.session_id,
      });
      toast.success("Shared to community · +10 FitPoints");
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share");
    } finally {
      setBusy(false);
    }
  };

  const saveToPhotos = async () => {
    try {
      const png = await renderPng();
      if (!png) return;
      const a = document.createElement("a");
      a.href = png;
      a.download = `fitfrik-workout-${session.session_id?.slice(0, 8) || Date.now()}.png`;
      a.click();
      toast.success("Saved to your downloads");
    } catch {
      toast.error("Could not save image");
    }
  };

  const nativeShare = async () => {
    try {
      const png = await renderPng();
      if (!png) return;
      // Convert dataURL to File for navigator.share
      const blob = await (await fetch(png)).blob();
      const file = new File([blob], "fitfrik-workout.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My FitFrik workout",
          text: caption,
        });
      } else {
        await saveToPhotos();
      }
    } catch (e) {
      // User cancelled — silent
      if (e?.name !== "AbortError") {
        await saveToPhotos();
      }
    }
  };

  const handleSkip = () => {
    onSkip?.();
    onClose?.();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[80] grid place-items-end bg-black/60"
      onClick={onClose}
      data-testid="workout-share-sheet"
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          background: "var(--bg-surface, #E4E2DE)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "16px 20px 32px",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(12,12,11,0.18)" }} />
        </div>

        {/* Card preview */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <WorkoutCard ref={cardRef} session={session} user={user} scale={0.62} />
        </div>

        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontFamily: "Barlow Condensed, sans-serif",
              fontSize: 22,
              fontWeight: 800,
              color: "var(--text-primary, #0C0C0B)",
            }}
          >
            Share your workout
          </div>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 200))}
            data-testid="workout-share-caption"
            maxLength={200}
            style={{
              width: "100%",
              marginTop: 10,
              padding: 12,
              background: "var(--bg-inset, #D8D6D1)",
              border: "none",
              borderRadius: 12,
              fontFamily: "Barlow",
              fontSize: 14,
              color: "var(--text-primary, #0C0C0B)",
              outline: "none",
              minHeight: 64,
              resize: "vertical",
            }}
          />
          <div style={{ textAlign: "right", fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
            {caption.length}/200
          </div>
        </div>

        <button
          onClick={shareToCommunity}
          disabled={busy}
          data-testid="workout-share-community"
          style={{
            width: "100%",
            height: 52,
            marginTop: 12,
            background: "#0C0C0B",
            color: "white",
            border: "none",
            borderRadius: 100,
            fontFamily: "Barlow",
            fontSize: 14,
            fontWeight: 700,
            cursor: busy ? "default" : "pointer",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Posting…" : "Share to Community"}
        </button>

        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button
            onClick={nativeShare}
            data-testid="workout-share-native"
            style={{
              flex: 1, height: 44,
              background: "transparent",
              color: "var(--text-primary, #0C0C0B)",
              border: "1px solid var(--border, rgba(12,12,11,0.16))",
              borderRadius: 100,
              fontFamily: "Barlow", fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <Share2 size={14} /> Share
          </button>
          <button
            onClick={saveToPhotos}
            data-testid="workout-save-png"
            style={{
              flex: 1, height: 44,
              background: "transparent",
              color: "var(--text-primary, #0C0C0B)",
              border: "1px solid var(--border, rgba(12,12,11,0.16))",
              borderRadius: 100,
              fontFamily: "Barlow", fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <Download size={14} /> Save PNG
          </button>
        </div>

        <button
          onClick={handleSkip}
          data-testid="workout-share-skip"
          style={{
            width: "100%", marginTop: 10, padding: 8,
            background: "transparent", border: "none",
            fontFamily: "Barlow", fontSize: 13,
            color: "var(--text-tertiary, #8A8884)",
          }}
        >
          Skip
        </button>

        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 16, right: 20,
            width: 32, height: 32, borderRadius: "50%",
            background: "var(--bg-inset, #D8D6D1)", border: "none",
            display: "grid", placeItems: "center",
          }}
        >
          <X size={16} />
        </button>
      </motion.div>
    </motion.div>
  );
}
